// src/pages/EditarCampanha.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import moment from 'moment-timezone';
import { ArrowLeftIcon, ArrowUpOnSquareIcon, ExclamationTriangleIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { getBearerHeaders } from '../lib/auth';
import { isAdminOrSuperAdmin } from '../lib/auth';

export default function EditarCampanha() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState(null);
    const [assistentes, setAssistentes] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [previewNumbers, setPreviewNumbers] = useState([]);
    const [planilhaData, setPlanilhaData] = useState([]);
    const [horaAtual, setHoraAtual] = useState(moment().tz('America/Sao_Paulo'));
    const [minDateTime, setMinDateTime] = useState(() => moment().tz('America/Sao_Paulo').startOf('minute').format('YYYY-MM-DDTHH:mm'));
    const [isAgendamentoFocused, setIsAgendamentoFocused] = useState(false);
    const [assistenteFiltroNome, setAssistenteFiltroNome] = useState('');
    const [binaFiltroNome, setBinaFiltroNome] = useState('');
    const [isAssistenteDropdownOpen, setIsAssistenteDropdownOpen] = useState(false);
    const [isBinaDropdownOpen, setIsBinaDropdownOpen] = useState(false);

    // const baseUrl = process.env.|REACT_APP_API_BASE_URL | 'http://localhost:3000/';
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
    const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || '{}'), []);
    const clienteId = useMemo(() => {
        const raw = userData?.clienteId || userData?._id || userData?.id;
        if (!raw) return '';
        if (typeof raw === 'object') {
            return String(raw._id || raw.id || '');
        }
        return String(raw);
    }, [userData]);
    const isAdmin = useMemo(() => isAdminOrSuperAdmin(userData), [userData]);
    const tokenClient = useMemo(() => localStorage.getItem('tokenClient') || '', []);

    const inputStyle = "w-full px-3 py-2 mt-1 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-500";
    const labelStyle = "text-sm font-medium text-gray-300";

    const formatPhoneNumber = (number) => {
        if (!number) return null;
        let cleaned = ('' + number).replace(/\D/g, '');
        if (cleaned.startsWith('55')) {
            if (!cleaned.startsWith('+55')) cleaned = `+${cleaned}`;
        } else {
            cleaned = `+55${cleaned}`;
        }
        if (cleaned.length !== 13) {
            while (cleaned.length < 13) {
                cleaned = cleaned.substring(0, 3) + '0' + cleaned.substring(3);
            }
        }
        return cleaned;
    };

    const assistentesFiltrados = useMemo(() => {
        if (!assistenteFiltroNome) return assistentes;
        const nomeLower = assistenteFiltroNome.toLowerCase();
        return assistentes.filter(a => (a.nome || a.name || '').toLowerCase().includes(nomeLower));
    }, [assistentes, assistenteFiltroNome]);

    const binasFiltradas = useMemo(() => {
        if (!binaFiltroNome) return phoneNumbers;
        const nomeLower = binaFiltroNome.toLowerCase();
        return phoneNumbers.filter(n =>
            (n.name || '').toLowerCase().includes(nomeLower) || (n.number || '').toLowerCase().includes(nomeLower)
        );
    }, [phoneNumbers, binaFiltroNome]);

    const assistenteDropdownRef = useRef(null);
    const binaDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (assistenteDropdownRef.current && !assistenteDropdownRef.current.contains(e.target)) setIsAssistenteDropdownOpen(false);
            if (binaDropdownRef.current && !binaDropdownRef.current.contains(e.target)) setIsBinaDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        const toastId = toast.loading('Carregando dados da campanha...');
        try {
            const authHeaders = getBearerHeaders();
            const params = !isAdmin && clienteId ? { clienteId } : {};
            const config = {
                params: { ...params, tokenClient },
                headers: {
                    ...(authHeaders || {})
                }
            };

            const [campanhaRes, assistentesRes, numerosRes, sipsRes] = await Promise.all([
                axios.get(`${baseUrl}/api/campanhas/${id}`, config),
                axios.get(`${baseUrl}/api/assistentes/master`, config),
                axios.get(`${baseUrl}/api/phoneNumbers`, config),
                axios.get(`${baseUrl}/api/sips`, config)
            ]);

            const campanhaData = campanhaRes.data;
            const numerosList = (numerosRes.data || []).map(pn => ({
                ...pn,
                sipAssociado: (sipsRes.data || []).find(sip => sip._id === (pn.credentialId?._id || pn.credentialId)) || null,
            }));

            const assistantId = campanhaData.sip?.phoneNumberBody?.assistantId || campanhaData.assistantId || '';
            const phoneNumberId = campanhaData.sip?.phoneNumberBody?.phoneNumberId || campanhaData.phoneNumberId || '';
            const numeroSelecionado = numerosList.find(n =>
                String(n._id) === String(phoneNumberId) || String(n.vapiId) === String(phoneNumberId)
            );

            const agendamentoFormatado = campanhaData.agendamento
                ? new Date(campanhaData.agendamento).toISOString().slice(0, 16)
                : moment().tz('America/Sao_Paulo').format('YYYY-MM-DDTHH:mm');

            const contatosStr = Array.isArray(campanhaData.contatos)
                ? campanhaData.contatos.join('\n')
                : (campanhaData.contatos || '');

            const func = campanhaData.funcionamento || {};
            const dataInicioFormatada = func.dataInicio
                ? moment(func.dataInicio).tz('America/Sao_Paulo').format('YYYY-MM-DD')
                : moment().tz('America/Sao_Paulo').format('YYYY-MM-DD');
            const dataFimFormatada = func.dataFim
                ? moment(func.dataFim).tz('America/Sao_Paulo').format('YYYY-MM-DD')
                : moment().tz('America/Sao_Paulo').add(1, 'month').format('YYYY-MM-DD');

            setFormData({
                ...campanhaData,
                nome: campanhaData.nome || '',
                objetivo: campanhaData.objetivo || '',
                tokenClient: campanhaData.tokenClient || '',
                contatos: contatosStr,
                agendamento: agendamentoFormatado,
                assistenteSelecionado: assistantId,
                phoneNumberSelecionado: numeroSelecionado?._id || '',
                qtdTentativasPorNumero: campanhaData.qtdTentativasPorNumero ?? 3,
                followUpConfig: {
                    habilitado: campanhaData.followUpConfig?.habilitado ?? false,
                    maxTentativasTotal: campanhaData.followUpConfig?.maxTentativasTotal ?? 4,
                    maxTentativasPorDia: campanhaData.followUpConfig?.maxTentativasPorDia ?? 1,
                    intervaloMinutos: campanhaData.followUpConfig?.intervaloMinutos ?? 1440,
                    horaInicioDisparos: campanhaData.followUpConfig?.horaInicioDisparos || '08:00',
                    horaFimDisparos: campanhaData.followUpConfig?.horaFimDisparos || '18:00',
                    reativarCicloAposLimiteAtivo: campanhaData.followUpConfig?.reativarCicloAposLimiteAtivo === true,
                    reativarCicloQuantidade: Number(campanhaData.followUpConfig?.reativarCicloQuantidade || 0),
                    reativarCicloUnidade: campanhaData.followUpConfig?.reativarCicloUnidade || 'semanas',
                    numerosExcluidos: Array.isArray(campanhaData.followUpConfig?.numerosExcluidos)
                        ? campanhaData.followUpConfig.numerosExcluidos.join('\n')
                        : '',
                    assistenteFollowUpId: campanhaData.followUpConfig?.assistenteFollowUpId || '',
                    phoneNumberFollowUpId: campanhaData.followUpConfig?.phoneNumberFollowUpId || '',
                },
                usarFuncionamento: !!(func.diasSemana?.length > 0),
                funcionamento: {
                    dataInicio: dataInicioFormatada,
                    dataFim: dataFimFormatada,
                    diasSemana: func.diasSemana || [1, 2, 3, 4, 5],
                    horaInicio: func.horaInicio || '08:00',
                    horaFim: func.horaFim || '18:00'
                }
            });
            setAssistentes(assistentesRes.data || []);
            setPhoneNumbers(numerosList);
            setPlanilhaData(campanhaData.dadosPlanilha || []);
            setPreviewNumbers(Array.isArray(campanhaData.contatos) ? campanhaData.contatos : []);
            setError('');
        } catch (err) {
            setError('Não foi possível carregar os dados para edição.');
            toast.error('Erro ao carregar dados.', { id: toastId });
            console.error(err);
        } finally {
            toast.dismiss(toastId);
            setLoading(false);
        }
    }, [id, clienteId, isAdmin, tokenClient, baseUrl]);

    useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

    useEffect(() => {
        const buscarHoraServidor = async () => {
            try {
                const res = await axios.get(`${baseUrl}/api/server-time`);
                if (res.data?.hora) {
                    const hora = moment(res.data.hora, 'YYYY-MM-DD HH:mm:ss').tz('America/Sao_Paulo');
                    setHoraAtual(hora);
                    if (!isAgendamentoFocused) {
                        setMinDateTime(prev => {
                            const novo = hora.clone().startOf('minute').format('YYYY-MM-DDTHH:mm');
                            return prev !== novo ? novo : prev;
                        });
                    }
                }
            } catch {
                const hora = moment().tz('America/Sao_Paulo');
                setHoraAtual(hora);
                setMinDateTime(hora.clone().startOf('minute').format('YYYY-MM-DDTHH:mm'));
            }
        };
        buscarHoraServidor();
        const interval = setInterval(buscarHoraServidor, 1000);
        return () => clearInterval(interval);
    }, [baseUrl, isAgendamentoFocused]);

    const toggleDiaSemana = (dia) => {
        setFormData(prev => {
            const dias = prev.funcionamento?.diasSemana || [];
            const novaLista = dias.includes(dia) ? dias.filter(d => d !== dia) : [...dias, dia].sort((a, b) => a - b);
            return { ...prev, funcionamento: { ...prev.funcionamento, diasSemana: novaLista } };
        });
    };

    const handleFuncionamentoChange = (field, value) => {
        setFormData(prev => ({ ...prev, funcionamento: { ...prev.funcionamento, [field]: value } }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'usarFuncionamento') {
            setFormData(prev => ({ ...prev, usarFuncionamento: e.target.checked }));
            return;
        }
        if (name.startsWith('funcionamento.')) {
            handleFuncionamentoChange(name.split('.')[1], value);
            return;
        }
        if (name.startsWith('followUpConfig.')) {
            const field = name.split('.')[1];
            const numericFields = new Set(['maxTentativasTotal', 'maxTentativasPorDia', 'intervaloMinutos', 'reativarCicloQuantidade']);
            const fieldValue =
                field === 'habilitado'
                    ? e.target.checked
                    : numericFields.has(field)
                        ? Number(value)
                        : value;
            setFormData(prev => ({
                ...prev,
                followUpConfig: {
                    ...prev.followUpConfig,
                    [field]: field === 'habilitado' ? fieldValue : numericFields.has(field) ? Number(value) : value
                }
            }));
            return;
        }
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === 'agendamento') {
                const dataAgendamento = moment(value).tz('America/Sao_Paulo').startOf('minute');
                const horaAtualMinuto = horaAtual.clone().startOf('minute');
                if (dataAgendamento.isBefore(horaAtualMinuto)) {
                    toast.error('Não é possível agendar no passado.');
                    newData.agendamento = horaAtualMinuto.format('YYYY-MM-DDTHH:mm');
                }
            }
            return newData;
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        const reader = new FileReader();
        reader.onload = (ev) => {
            const content = ev.target.result;
            if (ext === 'csv') {
                Papa.parse(content, {
                    header: true,
                    encoding: 'UTF-8',
                    skipEmptyLines: true,
                    complete: (r) => {
                        const data = r.data.filter(row => row && Object.keys(row).length > 0);
                        const headers = Object.keys(data[0] || {});
                        const phoneColumns = headers.filter(h => String(h).toLowerCase().includes('telefone'));
                        if (phoneColumns.length === 0) {
                            toast.error('Nenhuma coluna de telefones encontrada.');
                            return;
                        }
                        const numbers = new Set();
                        data.forEach(row => {
                            phoneColumns.forEach(col => {
                                const val = row[col];
                                if (val) {
                                    const f = formatPhoneNumber(String(val).trim());
                                    if (f) numbers.add(f);
                                }
                            });
                        });
                        if (numbers.size === 0) {
                            toast.error('Nenhum número válido encontrado.');
                            return;
                        }
                        const arr = Array.from(numbers);
                        setPlanilhaData(data);
                        setPreviewNumbers(arr);
                        setFormData(prev => ({ ...prev, contatos: arr.join('\n') }));
                        toast.success(`${arr.length} número(s) carregado(s).`);
                    }
                });
            } else if (ext === 'xlsx' || ext === 'xls') {
                try {
                    const wb = XLSX.read(content, { type: 'binary' });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                    const headers = (jsonData[0] || []).map(h => String(h || '').trim());
                    const rows = jsonData.slice(1);
                    const data = rows.map(row => {
                        const obj = {};
                        headers.forEach((h, i) => { if (h) obj[h] = row[i] !== undefined ? String(row[i] || '').trim() : ''; });
                        return obj;
                    }).filter(o => Object.keys(o).length > 0);
                    setPlanilhaData(data);
                    const phoneIndices = headers.map((h, i) => (String(h).toLowerCase().includes('telefone') ? i : null)).filter(x => x != null);
                    if (phoneIndices.length === 0) {
                        toast.error('Nenhuma coluna de telefones encontrada.');
                        return;
                    }
                    const nums = new Set();
                    rows.forEach(row => {
                        phoneIndices.forEach(i => {
                            const val = row[i];
                            if (val) {
                                const f = formatPhoneNumber(String(val).trim());
                                if (f) nums.add(f);
                            }
                        });
                    });
                    if (nums.size === 0) {
                        toast.error('Nenhum número válido encontrado.');
                        return;
                    }
                    const arr = Array.from(nums);
                    setPreviewNumbers(arr);
                    setFormData(prev => ({ ...prev, contatos: arr.join('\n') }));
                    toast.success(`${arr.length} número(s) carregado(s).`);
                } catch (err) {
                    toast.error('Erro ao processar o arquivo.');
                    console.error(err);
                }
            } else {
                toast.error('Use CSV ou XLSX.');
            }
        };
        if (ext === 'csv') reader.readAsText(file, 'UTF-8');
        else reader.readAsBinaryString(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { nome, objetivo, tokenClient: tokenClientCampanha, contatos, agendamento, assistenteSelecionado, phoneNumberSelecionado, qtdTentativasPorNumero, followUpConfig, usarFuncionamento, funcionamento } = formData;

        if (!nome?.trim()) return toast.error('Nome é obrigatório.');
        if (!objetivo?.trim()) return toast.error('Objetivo é obrigatório.');
        if (!tokenClientCampanha?.trim()) return toast.error('Token Client é obrigatório.');
        if (!contatos?.trim()) return toast.error('Lista de contatos é obrigatória.');
        if (!assistenteSelecionado) return toast.error('Selecione um assistente.');
        if (!phoneNumberSelecionado) return toast.error('Selecione um número de origem (Bina).');
        if (usarFuncionamento && (!funcionamento?.diasSemana?.length)) return toast.error('Selecione pelo menos um dia da semana.');

        const contatosArray = contatos.split('\n').map(c => c.trim()).filter(c => c);
        if (contatosArray.length === 0) return toast.error('Forneça ao menos um contato válido.');
        if (followUpConfig?.habilitado) {
            if ((Number(followUpConfig.maxTentativasTotal) || 0) < 1) return toast.error('Follow-up: máximo total deve ser ao menos 1.');
            if ((Number(followUpConfig.maxTentativasPorDia) || 0) < 1) return toast.error('Follow-up: máximo por dia deve ser ao menos 1.');
            if ((Number(followUpConfig.intervaloMinutos) || 0) < 1) return toast.error('Follow-up: intervalo mínimo é 1 minuto.');
            if (!/^\d{2}:\d{2}$/.test(String(followUpConfig.horaInicioDisparos || ''))) return toast.error('Follow-up: horário de início inválido.');
            if (!/^\d{2}:\d{2}$/.test(String(followUpConfig.horaFimDisparos || ''))) return toast.error('Follow-up: horário de fim inválido.');
            if (followUpConfig.reativarCicloAposLimiteAtivo && (Number(followUpConfig.reativarCicloQuantidade) || 0) < 1) {
                return toast.error('Follow-up: para reativar ciclo, informe uma quantidade maior que zero.');
            }
        }

        const dataAgendamento = moment(agendamento).tz('America/Sao_Paulo').startOf('minute');
        if (dataAgendamento.isBefore(horaAtual.clone().startOf('minute'))) {
            return toast.error('Não é possível agendar no passado.');
        }

        const assistenteObj = assistentes.find(a => a.vapiId === assistenteSelecionado);
        const phoneNumberObj = phoneNumbers.find(pn => pn._id === phoneNumberSelecionado || String(pn._id) === String(phoneNumberSelecionado));

        if (!assistenteObj || !phoneNumberObj || !phoneNumberObj.sipAssociado) {
            return toast.error('Assistente ou número de origem inválido.');
        }

        const phoneNumberIdToUse = phoneNumberObj.vapiId || phoneNumberObj._id;
        setIsSubmitting(true);
        const toastId = toast.loading('Atualizando campanha...');

        const payload = {
            nome: nome.trim(),
            objetivo: objetivo.trim(),
            tokenClient: tokenClientCampanha.trim(),
            contatos: contatosArray,
            agendamento: dataAgendamento.format(),
            assistantId: assistenteObj.vapiId,
            phoneNumberId: phoneNumberIdToUse,
            qtdTentativasPorNumero: qtdTentativasPorNumero ?? 3,
            followUpConfig: {
                habilitado: !!followUpConfig?.habilitado,
                maxTentativasTotal: Number(followUpConfig?.maxTentativasTotal || 4),
                maxTentativasPorDia: Number(followUpConfig?.maxTentativasPorDia || 1),
                intervaloMinutos: Number(followUpConfig?.intervaloMinutos || 1440),
                horaInicioDisparos: followUpConfig?.horaInicioDisparos || '08:00',
                horaFimDisparos: followUpConfig?.horaFimDisparos || '18:00',
                reativarCicloAposLimiteAtivo: followUpConfig?.reativarCicloAposLimiteAtivo === true,
                reativarCicloQuantidade: Number(followUpConfig?.reativarCicloQuantidade || 0),
                reativarCicloUnidade: followUpConfig?.reativarCicloUnidade || 'semanas',
                numerosExcluidos: String(followUpConfig?.numerosExcluidos || '')
                    .split(/\r?\n/)
                    .map((x) => x.trim())
                    .filter(Boolean),
                assistenteFollowUpId: followUpConfig?.assistenteFollowUpId?.trim() || null,
                phoneNumberFollowUpId: followUpConfig?.phoneNumberFollowUpId?.trim() || null
            },
            dadosPlanilha: planilhaData || [],
            sip: { qtdLigacoesSimultaneas: phoneNumberObj.sipAssociado?.qtdLigacoesSimultaneas },
            funcionamento: usarFuncionamento && funcionamento?.diasSemana?.length > 0 ? {
                dataInicio: funcionamento.dataInicio ? moment(funcionamento.dataInicio).startOf('day').format() : null,
                dataFim: funcionamento.dataFim ? moment(funcionamento.dataFim).endOf('day').format() : null,
                diasSemana: funcionamento.diasSemana || [],
                horaInicio: funcionamento.horaInicio || '08:00',
                horaFim: funcionamento.horaFim || '18:00'
            } : undefined
        };

        try {
            const authHeaders = getBearerHeaders();
            await axios.put(`${baseUrl}/api/campanhas/${id}`, payload, authHeaders ? { headers: authHeaders } : undefined);
            toast.success('Campanha atualizada com sucesso!', { id: toastId });
            navigate(`/campanhas/${id}`);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao atualizar campanha.';
            toast.error(msg, { id: toastId });
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderPreview = () => {
        if (previewNumbers.length === 0 && planilhaData.length === 0) return null;
        return (
            <div className="mt-4">
                {planilhaData.length > 0 && (
                    <div className="overflow-x-auto mb-4">
                        <table className="min-w-full bg-gray-700 rounded-lg">
                            <thead>
                                <tr>
                                    {Object.keys(planilhaData[0] || {}).map(h => (
                                        <th key={h} className="p-2 border-b border-gray-600">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {planilhaData.slice(0, 5).map((row, i) => (
                                    <tr key={i}>
                                        {Object.values(row).map((v, j) => (
                                            <td key={j} className="p-2 border-b border-gray-600">{v}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {previewNumbers.length > 0 && (
                    <div className="bg-gray-700 p-4 rounded-lg">
                        <p className="text-sm text-gray-300 mb-2">{previewNumbers.length} número(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                            {previewNumbers.slice(0, 12).map(n => (
                                <div key={n} className="bg-gray-800 p-2 rounded text-xs">{n}</div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <div className="p-8 text-white text-center">Carregando...</div>;
    if (error) return <div className="p-8 text-center text-red-400"><ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error}</div>;
    if (!formData) return null;

    return (
        <div className="space-y-6">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 mr-4 text-gray-400 hover:text-white rounded-full hover:bg-gray-700">
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <h1 className="text-3xl font-bold">Editar Campanha</h1>
                </div>

                <form onSubmit={handleSubmit} className="bg-gray-800 bg-opacity-60 p-8 rounded-xl shadow-lg space-y-6">
                    <div>
                        <label htmlFor="nome" className={labelStyle}>Nome da Campanha</label>
                        <input id="nome" name="nome" type="text" value={formData.nome || ''} onChange={handleChange} className={inputStyle} required placeholder="Ex: Prospecção de Leads - Agosto" />
                    </div>
                    <div>
                        <label htmlFor="objetivo" className={labelStyle}>Objetivo da Campanha</label>
                        <input id="objetivo" name="objetivo" type="text" value={formData.objetivo || ''} onChange={handleChange} className={inputStyle} required placeholder="Ex: qualificar lead, cobrança amigável" />
                    </div>
                    <div>
                        <label htmlFor="tokenClient" className={labelStyle}>Token Client</label>
                        <input id="tokenClient" name="tokenClient" type="text" value={formData.tokenClient || ''} onChange={handleChange} className={inputStyle} required placeholder="Cole o token que será usado nas ligações desta campanha" />
                        <p className="text-xs text-gray-500 mt-1">Esse token será salvo na campanha e usado junto com o assistente e a bina selecionados.</p>
                    </div>
                    <div>
                        <label htmlFor="contatos" className={labelStyle}>Lista de Contatos (um por linha)</label>
                        <textarea id="contatos" name="contatos" value={formData.contatos || ''} onChange={handleChange} className={`${inputStyle} h-40 font-mono`} placeholder="Cole seus contatos. Ex: +5511999998888" required />
                    </div>
                    <div>
                        <label htmlFor="file-upload" className={labelStyle}>Ou faça upload de um arquivo CSV/XLSX</label>
                        <input id="file-upload" name="file-upload" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className={inputStyle} />
                        {renderPreview()}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative" ref={assistenteDropdownRef}>
                            <label className={labelStyle}>Assistente de Voz</label>
                            <button type="button" onClick={() => setIsAssistenteDropdownOpen(!isAssistenteDropdownOpen)} className={`${inputStyle} w-full text-left flex items-center justify-between`}>
                                <span>{formData.assistenteSelecionado ? (assistentes.find(a => a.vapiId === formData.assistenteSelecionado)?.nome || assistentes.find(a => a.vapiId === formData.assistenteSelecionado)?.name || 'Selecione') : 'Selecione um assistente'}</span>
                                <ChevronDownIcon className={`h-5 w-5 ${isAssistenteDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isAssistenteDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-64 overflow-hidden">
                                    <div className="p-2 border-b border-gray-600">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                            <input type="text" placeholder="Buscar..." value={assistenteFiltroNome} onChange={e => setAssistenteFiltroNome(e.target.value)} className="w-full pl-8 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" onClick={e => e.stopPropagation()} />
                                        </div>
                                    </div>
                                    <div className="overflow-y-auto max-h-48">
                                        {assistentesFiltrados.length === 0 ? <div className="p-3 text-gray-400 text-sm text-center">Nenhum assistente</div> : assistentesFiltrados.map(a => (
                                            <button key={a.vapiId} type="button" onClick={() => { setFormData(prev => ({ ...prev, assistenteSelecionado: a.vapiId })); setIsAssistenteDropdownOpen(false); setAssistenteFiltroNome(''); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 ${formData.assistenteSelecionado === a.vapiId ? 'bg-purple-600 text-white' : 'text-gray-200'}`}>
                                                {a.nome || a.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="relative" ref={binaDropdownRef}>
                            <label className={labelStyle}>Número de Origem (Bina)</label>
                            <button type="button" onClick={() => setIsBinaDropdownOpen(!isBinaDropdownOpen)} className={`${inputStyle} w-full text-left flex items-center justify-between`}>
                                <span>{formData.phoneNumberSelecionado ? (() => { const s = phoneNumbers.find(n => n._id === formData.phoneNumberSelecionado || String(n._id) === String(formData.phoneNumberSelecionado)); return s ? `${s.name} (${s.number})` : 'Selecione'; })() : 'Selecione um número'}</span>
                                <ChevronDownIcon className={`h-5 w-5 ${isBinaDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isBinaDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-64 overflow-hidden">
                                    <div className="p-2 border-b border-gray-600">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                            <input type="text" placeholder="Buscar..." value={binaFiltroNome} onChange={e => setBinaFiltroNome(e.target.value)} className="w-full pl-8 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm" onClick={e => e.stopPropagation()} />
                                        </div>
                                    </div>
                                    <div className="overflow-y-auto max-h-48">
                                        {binasFiltradas.length === 0 ? <div className="p-3 text-gray-400 text-sm text-center">Nenhum número</div> : binasFiltradas.map(n => (
                                            <button key={n._id} type="button" disabled={!n.sipAssociado} onClick={() => { if (n.sipAssociado) { setFormData(prev => ({ ...prev, phoneNumberSelecionado: n._id })); setIsBinaDropdownOpen(false); setBinaFiltroNome(''); } }} className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 ${(formData.phoneNumberSelecionado === n._id || String(formData.phoneNumberSelecionado) === String(n._id)) ? 'bg-purple-600 text-white' : 'text-gray-200'} ${!n.sipAssociado ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                {n.name} ({n.number}) {!n.sipAssociado && '(Sem SIP)'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="agendamento" className={labelStyle}>Agendar Envio</label>
                        <div className="mb-3 p-3 bg-gray-700 border border-gray-600 rounded-md">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-300">Hora do servidor:</span>
                                <span className="font-semibold text-purple-400">{horaAtual.format('DD/MM/YYYY HH:mm:ss')}</span>
                            </div>
                        </div>
                        <input id="agendamento" name="agendamento" type="datetime-local" value={formData.agendamento || ''} onChange={handleChange} onFocus={() => setIsAgendamentoFocused(true)} onBlur={() => setIsAgendamentoFocused(false)} min={minDateTime} className={inputStyle} required />
                    </div>

                    <div className="border border-gray-600 rounded-lg p-4 bg-gray-700 bg-opacity-40">
                        <div className="flex items-center gap-2 mb-3">
                            <input type="checkbox" id="usarFuncionamento" name="usarFuncionamento" checked={formData.usarFuncionamento || false} onChange={handleChange} className="rounded border-gray-500 text-purple-600" />
                            <label htmlFor="usarFuncionamento" className={`${labelStyle} cursor-pointer`}>Definir horário de funcionamento</label>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Fora do período a campanha ficará pausada automaticamente.</p>
                        {formData.usarFuncionamento && (
                            <div className="space-y-4 pl-6 border-l-2 border-purple-500">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelStyle}>Período - Data Início</label>
                                        <input type="date" name="funcionamento.dataInicio" value={formData.funcionamento?.dataInicio || ''} onChange={handleChange} className={inputStyle} />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Período - Data Fim</label>
                                        <input type="date" name="funcionamento.dataFim" value={formData.funcionamento?.dataFim || ''} onChange={handleChange} className={inputStyle} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelStyle}>Dias da semana</label>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {[{ val: 0, lbl: 'Dom' }, { val: 1, lbl: 'Seg' }, { val: 2, lbl: 'Ter' }, { val: 3, lbl: 'Qua' }, { val: 4, lbl: 'Qui' }, { val: 5, lbl: 'Sex' }, { val: 6, lbl: 'Sáb' }].map(({ val, lbl }) => (
                                            <button key={val} type="button" onClick={() => toggleDiaSemana(val)} className={`px-3 py-2 rounded-md text-sm font-medium ${(formData.funcionamento?.diasSemana || []).includes(val) ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'}`}>{lbl}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelStyle}>Horário Início</label>
                                        <input type="time" name="funcionamento.horaInicio" value={formData.funcionamento?.horaInicio || '08:00'} onChange={handleChange} className={inputStyle} />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Horário Fim</label>
                                        <input type="time" name="funcionamento.horaFim" value={formData.funcionamento?.horaFim || '18:00'} onChange={handleChange} className={inputStyle} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="qtdTentativasPorNumero" className={labelStyle}>Número de Tentativas por Número</label>
                        <select id="qtdTentativasPorNumero" name="qtdTentativasPorNumero" value={formData.qtdTentativasPorNumero ?? 3} onChange={handleChange} className={inputStyle} required>
                            {[1, 2, 3, 4, 5].map(num => <option key={num} value={num}>{num} tentativas</option>)}
                        </select>
                    </div>
                    <div className="border border-gray-600 rounded-lg p-4 bg-gray-700 bg-opacity-40">
                        <div className="flex items-center gap-2 mb-3">
                            <input
                                type="checkbox"
                                id="followUpConfig.habilitado"
                                name="followUpConfig.habilitado"
                                checked={!!formData.followUpConfig?.habilitado}
                                onChange={handleChange}
                                className="rounded border-gray-500 text-purple-600"
                            />
                            <label htmlFor="followUpConfig.habilitado" className={`${labelStyle} cursor-pointer`}>Ativar Follow-up automático</label>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">
                            Quando ativo, contatos sem atendimento/erro retentável entram em novas tentativas configuráveis.
                        </p>
                        {formData.followUpConfig?.habilitado && (
                            <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={labelStyle}>Máximo total</label>
                                    <input type="number" min="1" max="20" name="followUpConfig.maxTentativasTotal" value={formData.followUpConfig?.maxTentativasTotal ?? 4} onChange={handleChange} className={inputStyle} />
                                </div>
                                <div>
                                    <label className={labelStyle}>Máximo por dia</label>
                                    <input type="number" min="1" max="10" name="followUpConfig.maxTentativasPorDia" value={formData.followUpConfig?.maxTentativasPorDia ?? 1} onChange={handleChange} className={inputStyle} />
                                </div>
                                <div>
                                    <label className={labelStyle}>Intervalo (minutos)</label>
                                    <input type="number" min="1" max="10080" name="followUpConfig.intervaloMinutos" value={formData.followUpConfig?.intervaloMinutos ?? 1440} onChange={handleChange} className={inputStyle} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className={labelStyle}>Início dos disparos</label>
                                    <input
                                        type="time"
                                        name="followUpConfig.horaInicioDisparos"
                                        value={formData.followUpConfig?.horaInicioDisparos || '08:00'}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label className={labelStyle}>Fim dos disparos</label>
                                    <input
                                        type="time"
                                        name="followUpConfig.horaFimDisparos"
                                        value={formData.followUpConfig?.horaFimDisparos || '18:00'}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        id="followUpConfig.reativarCicloAposLimiteAtivo"
                                        name="followUpConfig.reativarCicloAposLimiteAtivo"
                                        type="checkbox"
                                        checked={!!formData.followUpConfig?.reativarCicloAposLimiteAtivo}
                                        onChange={handleChange}
                                        className="rounded border-gray-500 text-purple-600"
                                    />
                                    <label htmlFor="followUpConfig.reativarCicloAposLimiteAtivo" className={labelStyle}>Reativar ciclo após atingir limite</label>
                                </div>
                                {formData.followUpConfig?.reativarCicloAposLimiteAtivo && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelStyle}>Reativar após</label>
                                            <input type="number" min="1" max="120" name="followUpConfig.reativarCicloQuantidade" value={formData.followUpConfig?.reativarCicloQuantidade ?? 0} onChange={handleChange} className={inputStyle} />
                                        </div>
                                        <div>
                                            <label className={labelStyle}>Unidade</label>
                                            <select name="followUpConfig.reativarCicloUnidade" value={formData.followUpConfig?.reativarCicloUnidade || 'semanas'} onChange={handleChange} className={inputStyle}>
                                                <option value="dias">Dias</option>
                                                <option value="semanas">Semanas</option>
                                                <option value="meses">Meses</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4">
                                <label className={labelStyle}>Números excluídos do Follow-up</label>
                                <textarea
                                    name="followUpConfig.numerosExcluidos"
                                    value={formData.followUpConfig?.numerosExcluidos || ''}
                                    onChange={handleChange}
                                    className={inputStyle}
                                    placeholder="Um número por linha. Esses contatos não entrarão no Follow-up."
                                    rows={4}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className={labelStyle}>Assistente no Follow-up</label>
                                    <select
                                        name="followUpConfig.assistenteFollowUpId"
                                        value={formData.followUpConfig?.assistenteFollowUpId || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    >
                                        <option value="">Mesmo assistente da campanha</option>
                                        {assistentes.map((a) => (
                                            <option key={a.vapiId || a._id} value={a.vapiId || a._id}>{a.nome || a.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelStyle}>Número (bina) no Follow-up</label>
                                    <select
                                        name="followUpConfig.phoneNumberFollowUpId"
                                        value={formData.followUpConfig?.phoneNumberFollowUpId || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    >
                                        <option value="">Mesmo número da campanha</option>
                                        {phoneNumbers.map((pn) => (
                                            <option key={pn.vapiId || pn._id} value={pn.vapiId || pn._id}>{pn.name} ({pn.number})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Deixe em branco para repetir assistente e bina da campanha nas tentativas de follow-up.</p>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isSubmitting} className="flex items-center justify-center w-full md:w-auto px-6 py-3 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50">
                            <ArrowUpOnSquareIcon className="h-5 w-5 mr-2" />
                            {isSubmitting ? 'Atualizando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
